// Deterministic pseudo-random generator replacing C rand()/srand().
//
// The original relies on the C standard library rand(). We only require
// determinism *from a given seed* so that map generation reproduces exactly
// when a save is reloaded (saves store the seed and regenerate the dungeon).
// Gameplay randomness does not need to match any particular platform.
//
// mulberry32 gives a fast, well-distributed 32-bit stream; we expose it with a
// C-like surface (rand() returns a non-negative 31-bit integer, RAND_MAX-ish).

import type { World } from './world';
import { getActiveWorld } from './world';

let state = 1 >>> 0;

export const RAND_MAX = 0x7fffffff;

function persistState(world: World): void {
  world.rng.state = state >>> 0;
  world.g.seed = state;
}

export function syncRngFromWorld(world: World): void {
  state = world.rng.state >>> 0;
  if (state === 0) state = 0x9e3779b9;
  persistState(world);
}

export function srand(seed: number): void {
  state = seed >>> 0;
  if (state === 0) state = 0x9e3779b9;
  persistState(getActiveWorld());
}

export function rand(): number {
  state = (state + 0x6d2b79f5) >>> 0;
  let t = state;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  t = (t ^ (t >>> 14)) >>> 0;
  persistState(getActiveWorld());
  return t & 0x7fffffff;
}

// mapgen.c: rndnum(max) => rand() % (max+1)
export function rndnum(max: number): number {
  return rand() % (max + 1);
}

// mapgen.c: rndval(a, b) => inclusive random in [a, b]
export function rndval(a: number, b: number): number {
  if (a === b) return a;
  if (b < a) {
    const temp = a;
    a = b;
    b = temp;
  }
  return rndnum(b - a) + a;
}

export function randomSeed(): number {
  return (Math.floor(Math.random() * 0xffffffff) >>> 0) || 1;
}

/** Test-only: current RNG state after srand/rand. */
export function getRngState(): number {
  return state;
}

// levelblit.c RandomDir()
export function RandomDir(): number {
  return ((rand() % 256) * Math.PI * 2.0) / 256.0;
}
